import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';
import { getNoticeAuthorDisplay } from '../../utils/noticeAuthor';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const BackRow = styled.div`
  margin-bottom: 1rem;
`;

const BackLink = styled(Link)`
  font-size: 0.88rem;
  font-weight: 700;
  color: #4f46e5;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Article = styled.article`
  margin-top: 0.5rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.12rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 800;
  color: ${({ $type }) => ($type === '중요' ? '#b91c1c' : '#334155')};
  background: ${({ $type }) => ($type === '중요' ? '#fee2e2' : '#f1f5f9')};
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
  color: #0f172a;
  line-height: 1.35;
`;

const TitlePin = styled.span`
  flex-shrink: 0;
  font-size: 1.2rem;
  line-height: 1;
`;

const AuthorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
`;

const Avatar = styled.span`
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  background: rgba(248, 250, 252, 0.95);
`;

const AuthorMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  min-width: 0;
`;

const AuthorName = styled.span`
  font-weight: 800;
  color: #0f172a;
`;

const AuthorLogin = styled.span`
  font-size: 0.78rem;
  color: #64748b;
`;

const MetaSep = styled.span`
  color: #cbd5e1;
  font-weight: 500;
  user-select: none;
`;

const DateLine = styled.p`
  margin: 0 0 1rem;
  font-size: 0.82rem;
  color: #64748b;
`;

const ViewCountLine = styled.span`
  margin-left: 0.65rem;
  padding-left: 0.65rem;
  border-left: 1px solid #e2e8f0;
  font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.95rem;
  line-height: 1.65;
  color: #1f2937;
`;

const CommentsSection = styled.section`
  margin-top: 1.75rem;
  padding-top: 1.35rem;
  border-top: 1px solid #e8ecf1;
`;

const CommentsTitle = styled.h3`
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.85rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #e8ecf1;
  background: #fafbfc;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  border: 1px solid #d8dee9;
  border-radius: 0.6rem;
  padding: 0.6rem 0.7rem;
  font-size: 0.9rem;
  resize: vertical;
  background: #fff;
  color: #1e293b;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: none;
    border-color: #a5b4fc;
    box-shadow: 0 0 0 3px rgba(165, 180, 252, 0.25);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 0.6rem;
  padding: 0.45rem 0.9rem;
  font-weight: 700;
  font-size: 0.86rem;
  cursor: pointer;
  background: rgba(99, 102, 241, 0.95);
  color: #f8fafc;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid #d8dee9;
  border-radius: 0.5rem;
  padding: 0.32rem 0.65rem;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  background: #fff;
  color: #64748b;
  &:hover {
    border-color: #c7d2fe;
    color: #4f46e5;
    background: #fafbff;
  }
`;

const CommentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const CommentItem = styled.li`
  padding: 0;
`;

const CommentInner = styled.div`
  padding-left: ${({ $depth }) => `${Math.min($depth, 6) * 0.85}rem`};
  border-left: ${({ $depth }) => ($depth > 0 ? '2px solid #e0e7ff' : 'none')};
`;

const CommentCard = styled.div`
  border: 1px solid #e8ecf1;
  border-radius: 0.75rem;
  background: #fafbfc;
  padding: 0.85rem 1rem;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &:hover {
    border-color: #dce3ed;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
`;

const CommentGrid = styled.div`
  display: grid;
  grid-template-columns: 2.35rem minmax(0, 1fr) auto;
  grid-template-areas:
    'cgav cgauth cgaside'
    'cgav cgbody cgbody';
  column-gap: 0.65rem;
  row-gap: 0.35rem;
  align-items: start;
`;

const CommentGridAv = styled.div`
  grid-area: cgav;
  justify-self: start;
`;

const CommentGridAuth = styled.div`
  grid-area: cgauth;
  min-width: 0;
`;

const CommentGridAside = styled.div`
  grid-area: cgaside;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
`;

const SmallAvatar = styled.span`
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  background: #fff;
  flex-shrink: 0;
`;

const AuthorStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;
`;

const CommentBody = styled.div`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.55;
  color: #334155;
`;

const CommentGridBody = styled(CommentBody)`
  grid-area: cgbody;
  min-width: 0;
  padding-top: 0.15rem;
`;

const InlineReplyBox = styled.form`
  margin-top: 0.65rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.65rem;
  border: 1px solid #c7d2fe;
  background: #f8f9ff;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const InlineReplyTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: #4f46e5;
  text-transform: none;
`;

const CompactTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 72px;
  border: 1px solid #d8dee9;
  border-radius: 0.55rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.875rem;
  resize: vertical;
  background: #fff;
  color: #1e293b;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: none;
    border-color: #a5b4fc;
    box-shadow: 0 0 0 2px rgba(165, 180, 252, 0.2);
  }
`;

const CommentDate = styled.span`
  font-size: 0.72rem;
  color: #94a3b8;
  white-space: nowrap;
`;

const ErrorText = styled.p`
  margin: 0.75rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
`;

const EmptyComments = styled.p`
  margin: 0;
  padding: 0.5rem 0 0.25rem;
  color: #64748b;
  font-size: 0.88rem;
`;

const ReplyBottomHint = styled.div`
  margin-top: 0.85rem;
  padding: 0.75rem 1rem;
  border-radius: 0.65rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 0.82rem;
  color: #475569;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const samePortalUserId = (portalUserId, authorUserId) => {
  if (portalUserId == null || authorUserId == null) return false;
  return Number(portalUserId) === Number(authorUserId);
};

const formatDt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

const buildCommentTree = (flat) => {
  const byParent = new Map();
  flat.forEach((c) => {
    const pid = c.parent_comment_id ?? null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(c);
  });
  const walk = (parentId, depth) => {
    const kids = byParent.get(parentId) || [];
    return kids.flatMap((node) => [{ node, depth }, ...walk(node.id, depth + 1)]);
  };
  return walk(null, 0);
};

const PortalNoticeDetailPage = () => {
  const { noticeId } = useParams();
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [notice, setNotice] = useState(null);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [commentError, setCommentError] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentDraft, setEditCommentDraft] = useState('');
  const viewCountGateRef = useRef({ noticeKey: '', counted: false, pendingBump: false });

  const load = useCallback(
    async ({ recordView = true } = {}) => {
      if (!portalUser?.login_id || !noticeId) return;
      let bumpView = recordView;
      let lockedPendingBump = false;
      if (bumpView) {
        const key = `${noticeId}:${portalUser.login_id}`;
        const g = viewCountGateRef.current;
        if (g.noticeKey !== key) {
          viewCountGateRef.current = { noticeKey: key, counted: false, pendingBump: false };
        }
        const g2 = viewCountGateRef.current;
        if (g2.counted || g2.pendingBump) {
          bumpView = false;
        } else {
          g2.pendingBump = true;
          lockedPendingBump = true;
        }
      }
      setError('');
      try {
        const qs = new URLSearchParams({
          login_id: portalUser.login_id,
          record_view: bumpView ? 'true' : 'false',
        });
        const data = await request({
          endpoint: `${BACKEND_BASE_URL}/api/notices/${encodeURIComponent(
            noticeId,
          )}?${qs.toString()}`,
        });
        setNotice(data?.notice || null);
        setComments(data?.comments || []);
        if (bumpView) {
          viewCountGateRef.current.counted = true;
        }
      } catch (e) {
        setNotice(null);
        setComments([]);
        setError(e?.responseData?.message || '공지를 불러오지 못했습니다.');
      } finally {
        if (lockedPendingBump) {
          viewCountGateRef.current.pendingBump = false;
        }
      }
    },
    [noticeId, portalUser?.login_id, request],
  );

  useEffect(() => {
    load({ recordView: true });
  }, [load]);

  const threaded = useMemo(() => buildCommentTree(comments), [comments]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setCommentError('');
    const text = commentText.trim();
    if (!text) return;
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/notices/${encodeURIComponent(noticeId)}/comments`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          content: text,
          parent_comment_id: replyTo,
        },
      });
      setCommentText('');
      setReplyTo(null);
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 등록에 실패했습니다.');
    }
  };

  const beginCommentEdit = (c) => {
    setCommentError('');
    setEditingCommentId(c.id);
    setEditCommentDraft(c.content || '');
    setReplyTo(null);
    setCommentText('');
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditCommentDraft('');
    setCommentError('');
  };

  const handleSaveNoticeComment = async (e, commentId) => {
    e.preventDefault();
    setCommentError('');
    const text = editCommentDraft.trim();
    if (!text) return;
    try {
      await request({
        method: 'PUT',
        endpoint: `${BACKEND_BASE_URL}/api/notices/comments/${encodeURIComponent(commentId)}`,
        body: { login_id: portalUser.login_id, content: text },
      });
      setEditingCommentId(null);
      setEditCommentDraft('');
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 수정에 실패했습니다.');
    }
  };

  const handleDeleteNoticeComment = async (commentId) => {
    if (!window.confirm('이 댓글을 삭제할까요?')) return;
    setCommentError('');
    try {
      await request({
        method: 'DELETE',
        endpoint: `${BACKEND_BASE_URL}/api/notices/comments/${encodeURIComponent(commentId)}`,
        body: { login_id: portalUser.login_id },
      });
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditCommentDraft('');
      }
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 삭제에 실패했습니다.');
    }
  };

  if (!notice && !error) {
    return (
      <Wrapper>
        <BackRow>
          <BackLink to="/portal/notices">← 목록</BackLink>
        </BackRow>
        <SectionHeading eyebrow="NOTICE" title="공지사항" />
        <p style={{ color: '#64748b' }}>{loading ? '불러오는 중...' : ''}</p>
      </Wrapper>
    );
  }

  if (error && !notice) {
    return (
      <Wrapper>
        <BackRow>
          <BackLink to="/portal/notices">← 목록</BackLink>
        </BackRow>
        <SectionHeading eyebrow="NOTICE" title="공지사항" />
        <ErrorText>{error}</ErrorText>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <BackRow>
        <BackLink to="/portal/notices">← 공지 목록</BackLink>
      </BackRow>

      <Article>
        <MetaRow>
          <TypeBadge $type={notice.notice_type}>{notice.notice_type}</TypeBadge>
        </MetaRow>
        <Title>
          {notice.is_pinned ? (
            <TitlePin title="상단 고정" aria-label="상단 고정" role="img">
              📌
            </TitlePin>
          ) : null}
          <span style={{ minWidth: 0 }}>{notice.title}</span>
        </Title>
        <AuthorWrap>
          <Avatar aria-hidden>{notice.author?.profile_emoji || '🙂'}</Avatar>
          <AuthorMeta>
            {(() => {
              const { primary, secondary } = getNoticeAuthorDisplay(notice.author);
              return (
                <>
                  <AuthorName>{primary}</AuthorName>
                  {secondary ? (
                    <>
                      <MetaSep aria-hidden>·</MetaSep>
                      <AuthorLogin>{secondary}</AuthorLogin>
                    </>
                  ) : null}
                </>
              );
            })()}
          </AuthorMeta>
        </AuthorWrap>
        <DateLine>
          작성 {formatDt(notice.created_at)}
          <ViewCountLine>조회 {notice.view_count ?? 0}</ViewCountLine>
        </DateLine>
        <Body>{notice.content}</Body>
      </Article>

      <CommentsSection>
        <CommentsTitle>댓글 {comments.length}</CommentsTitle>

        {threaded.length === 0 ? (
          <EmptyComments>첫 댓글을 남겨 보세요.</EmptyComments>
        ) : (
          <CommentList>
            {threaded.map(({ node: c, depth }) => {
              const isCAuthor = samePortalUserId(portalUser?.id, c.author?.user_id);
              return (
                <CommentItem key={c.id}>
                  <CommentInner $depth={depth}>
                    <CommentCard>
                      <CommentGrid>
                        <CommentGridAv>
                          <SmallAvatar aria-hidden>{c.author?.profile_emoji || '🙂'}</SmallAvatar>
                        </CommentGridAv>
                        <CommentGridAuth>
                          <AuthorStack>
                            {(() => {
                              const { primary, secondary } = getNoticeAuthorDisplay(c.author);
                              return (
                                <>
                                  <AuthorName style={{ fontSize: '0.875rem' }}>{primary}</AuthorName>
                                  {secondary ? <AuthorLogin>{secondary}</AuthorLogin> : null}
                                </>
                              );
                            })()}
                          </AuthorStack>
                        </CommentGridAuth>
                        <CommentGridAside>
                          <CommentDate>{formatDt(c.created_at)}</CommentDate>
                          {isCAuthor && editingCommentId !== c.id ? (
                            <>
                              <GhostButton type="button" disabled={loading} onClick={() => beginCommentEdit(c)}>
                                수정
                              </GhostButton>
                              <GhostButton
                                type="button"
                                disabled={loading}
                                onClick={() => handleDeleteNoticeComment(c.id)}
                              >
                                삭제
                              </GhostButton>
                            </>
                          ) : null}
                          {!editingCommentId ? (
                            <GhostButton
                              type="button"
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditCommentDraft('');
                                setReplyTo(c.id);
                                setCommentText('');
                                setCommentError('');
                              }}
                            >
                              답글
                            </GhostButton>
                          ) : null}
                        </CommentGridAside>
                        {editingCommentId === c.id ? (
                          <span style={{ gridArea: 'cgbody', minHeight: '0.25rem' }} aria-hidden />
                        ) : (
                          <CommentGridBody>{c.content}</CommentGridBody>
                        )}
                      </CommentGrid>
                      {editingCommentId === c.id ? (
                        <InlineReplyBox as="form" onSubmit={(e) => handleSaveNoticeComment(e, c.id)}>
                          <InlineReplyTitle>댓글 수정</InlineReplyTitle>
                          <CompactTextArea
                            value={editCommentDraft}
                            onChange={(ev) => setEditCommentDraft(ev.target.value)}
                            placeholder="댓글 내용"
                            disabled={loading}
                            maxLength={4000}
                            aria-label="댓글 수정 내용"
                          />
                          <ButtonRow>
                            <PrimaryButton type="submit" disabled={loading}>
                              저장
                            </PrimaryButton>
                            <GhostButton type="button" disabled={loading} onClick={cancelCommentEdit}>
                              취소
                            </GhostButton>
                            {commentError ? (
                              <ErrorText as="span" style={{ margin: 0, flex: '1 1 100%' }}>
                                {commentError}
                              </ErrorText>
                            ) : null}
                          </ButtonRow>
                        </InlineReplyBox>
                      ) : null}
                    </CommentCard>
                    {replyTo === c.id && !editingCommentId ? (
                      <InlineReplyBox onSubmit={handleCommentSubmit}>
                        <InlineReplyTitle>답글 작성</InlineReplyTitle>
                        <CompactTextArea
                          value={commentText}
                          onChange={(ev) => setCommentText(ev.target.value)}
                          placeholder="답글을 입력하세요."
                          disabled={loading}
                          aria-label="답글 내용"
                        />
                        <ButtonRow>
                          <PrimaryButton type="submit" disabled={loading}>
                            답글 등록
                          </PrimaryButton>
                          <GhostButton
                            type="button"
                            onClick={() => {
                              setReplyTo(null);
                              setCommentText('');
                              setCommentError('');
                            }}
                          >
                            취소
                          </GhostButton>
                          {commentError ? (
                            <ErrorText as="span" style={{ margin: 0, flex: '1 1 100%' }}>
                              {commentError}
                            </ErrorText>
                          ) : null}
                        </ButtonRow>
                      </InlineReplyBox>
                    ) : null}
                  </CommentInner>
                </CommentItem>
              );
            })}
          </CommentList>
        )}

        {replyTo && !editingCommentId ? (
          <ReplyBottomHint>
            <span>답글 입력란이 열려 있어요.</span>
            <GhostButton
              type="button"
              onClick={() => {
                setReplyTo(null);
                setCommentText('');
                setCommentError('');
              }}
            >
              취소하고 댓글 작성
            </GhostButton>
          </ReplyBottomHint>
        ) : editingCommentId ? (
          <ReplyBottomHint>
            <span>댓글 수정 중이에요.</span>
            <GhostButton type="button" onClick={cancelCommentEdit}>
              수정 취소
            </GhostButton>
          </ReplyBottomHint>
        ) : (
          <CommentForm onSubmit={handleCommentSubmit}>
            <TextArea
              value={commentText}
              onChange={(ev) => setCommentText(ev.target.value)}
              placeholder="댓글을 입력하세요."
              disabled={loading}
            />
            <ButtonRow>
              <PrimaryButton type="submit" disabled={loading}>
                댓글 등록
              </PrimaryButton>
              {commentError ? <ErrorText as="span">{commentError}</ErrorText> : null}
            </ButtonRow>
          </CommentForm>
        )}
      </CommentsSection>
    </Wrapper>
  );
};

export default PortalNoticeDetailPage;
